import React, { useState, useEffect } from 'react';
import { approvalsAPI, jobsAPI } from '../../utils/api';
import { Drawer } from '../shared/DrawerManager';
import toastr from 'toastr';

const ApprovalRequestModal = ({ application, jobId, onClose, onRequested }) => {
  const [requestType, setRequestType] = useState('shortlist');
  const [requestedFor, setRequestedFor] = useState('');
  const [selectedUser, setSelectedUser] = useState(null); // Store selected user object
  const [message, setMessage] = useState('');
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Search if query is at least 2 characters
    if (searchQuery.length >= 2) {
      // Only search if no user is selected, or if the query doesn't match the selected user's name
      const selectedUserMatch = selectedUser && (
        searchQuery.toLowerCase() === (selectedUser.name || selectedUser.displayName || selectedUser.email || '').toLowerCase()
      );
      
      if (!selectedUserMatch) {
        searchUsers();
      } else {
        // Query matches selected user, don't search
        setAvailableUsers([]);
      }
    } else {
      setAvailableUsers([]);
    }
  }, [searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const searchUsers = async () => {
    setLoadingUsers(true);
    try {
      console.log('[ApprovalRequestModal] Searching users with query:', searchQuery);
      const result = await jobsAPI.searchAzureADUsers(searchQuery);
      console.log('[ApprovalRequestModal] Search result:', result);
      
      // Filter out candidates and external users
      const allUsers = result.data?.users || result.users || [];
      console.log('[ApprovalRequestModal] All users from API:', allUsers);
      
      const internalUsers = allUsers.filter(user => {
        const role = user.role || user.jobTitle?.toLowerCase();
        const isInternal = ['recruiter', 'interviewer', 'admin', 'stakeholder', 'trainer'].some(r => 
          role?.includes(r) || user.email?.includes('securelayer7.net')
        );
        return isInternal;
      });
      
      console.log('[ApprovalRequestModal] Filtered internal users:', internalUsers);
      setAvailableUsers(internalUsers);
    } catch (error) {
      console.error('[ApprovalRequestModal] Failed to search users:', error);
      setAvailableUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleRequest = async () => {
    if (!requestedFor) {
      toastr.error('Please select a user to request approval from');
      return;
    }

    setSubmitting(true);
    try {
      await approvalsAPI.request({
        jobApplicationId: application._id,
        requestedFor,
        requestType,
        message: message.trim()
      });

      toastr.success('Approval request sent successfully!');
      if (onRequested) {
        onRequested();
      }
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('Failed to request approval:', error);
      if (error.response?.status === 409) {
        toastr.warning(error.response.data.error || 'A pending approval request already exists');
      } else {
        toastr.error(error.response?.data?.error || 'Failed to send approval request');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      isOpen={true}
      onClose={onClose}
      title="Request Approval"
      width="md"
      footer={
        <>
          <button
            type="button"
            className="drawer-btn drawer-btn-secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="drawer-btn drawer-btn-primary"
            onClick={handleRequest}
            disabled={submitting || !requestedFor}
          >
            {submitting ? 'Sending...' : 'Send Request'}
          </button>
        </>
      }
    >
      <div style={{ marginBottom: '1.5rem' }}>
        <p style={{ color: '#6B7280', fontSize: '0.875rem', marginBottom: '1rem' }}>
          Request approval from a manager or stakeholder to move <strong>{application.candidateInfo.firstName} {application.candidateInfo.lastName}</strong> to the next stage.
        </p>
      </div>

      {/* Request Type */}
      <div style={{ marginBottom: '1.5rem' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
          Request Type
        </label>
        <select
          value={requestType}
          onChange={(e) => setRequestType(e.target.value)}
          style={{
            width: '100%',
            padding: '0.625rem 0.875rem',
            border: '1px solid #D1D5DB',
            borderRadius: '6px',
            fontSize: '0.875rem',
            background: '#ffffff'
          }}
        >
          <option value="shortlist">Shortlist Candidate</option>
          <option value="interview">Schedule Interview</option>
          <option value="offer">Extend Offer</option>
          <option value="hire">Hire Candidate</option>
          <option value="other">Other</option>
        </select>
      </div>

      {/* Request Approval From */}
      <div style={{ marginBottom: '1.5rem' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
          Request Approval From <span style={{ color: '#EF4444' }}>*</span>
        </label>
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              // Clear selection if user starts typing (unless they're just editing)
              if (selectedUser && e.target.value !== (selectedUser.name || selectedUser.displayName || selectedUser.email || '')) {
                setSelectedUser(null);
                setRequestedFor('');
              }
            }}
            placeholder="Search for manager or stakeholder..."
            style={{
              width: '100%',
              padding: '0.625rem 0.875rem',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '0.875rem'
            }}
          />
          {(loadingUsers || (!loadingUsers && availableUsers.length > 0)) && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#FFFFFF', border: '1px solid #D1D5DB', borderRadius: '6px', marginTop: '0.25rem', maxHeight: '200px', overflowY: 'auto', zIndex: 1000, boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}>
              {loadingUsers ? (
                <div style={{ padding: '0.5rem', fontSize: '0.875rem', color: '#6B7280' }}>Searching...</div>
              ) : availableUsers.length > 0 ? (
                availableUsers.map(user => (
                  <button
                    key={user.id || user._id}
                    type="button"
                  onClick={() => {
                    // Prefer MongoDB _id, fallback to Azure AD id
                    const userId = user._id || user.id;
                    setRequestedFor(userId);
                    setSelectedUser(user); // Store the full user object
                    setSearchQuery(user.name || user.displayName || user.email || '');
                    setAvailableUsers([]); // Close dropdown
                    console.log('[ApprovalRequestModal] Selected user:', { 
                      userId, 
                      hasMongoId: !!user._id, 
                      hasAzureId: !!user.id,
                      email: user.email 
                    });
                  }}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      textAlign: 'left',
                      background: requestedFor === (user.id || user._id) ? '#EFF6FF' : '#FFFFFF',
                      border: 'none',
                      borderBottom: '1px solid #E5E7EB',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      color: '#374151'
                    }}
                    onMouseEnter={(e) => {
                      if (requestedFor !== (user.id || user._id)) {
                        e.target.style.background = '#F9FAFB';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (requestedFor !== (user.id || user._id)) {
                        e.target.style.background = '#FFFFFF';
                      }
                    }}
                  >
                    <div style={{ fontWeight: '500' }}>{user.name || user.displayName || user.email}</div>
                    {user.jobTitle && (
                      <div style={{ fontSize: '0.75rem', color: '#6B7280', marginTop: '0.25rem' }}>{user.jobTitle}</div>
                    )}
                  </button>
                ))
              ) : (
                <div style={{ padding: '0.5rem', fontSize: '0.875rem', color: '#6B7280' }}>No users found</div>
              )}
            </div>
          )}
        </div>
        {requestedFor && selectedUser && (
          <div style={{ marginTop: '0.5rem', padding: '0.5rem', background: '#EFF6FF', borderRadius: '4px', fontSize: '0.875rem', color: '#1E40AF' }}>
            Selected: <strong>{selectedUser.name || selectedUser.displayName || selectedUser.email}</strong>
            {selectedUser.jobTitle && <span style={{ color: '#6B7280', marginLeft: '0.5rem' }}>({selectedUser.jobTitle})</span>}
          </div>
        )}
      </div>

      {/* Message */}
      <div style={{ marginBottom: '1.5rem' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
          Message (Optional)
        </label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Add a message explaining why you need this approval..."
          rows={4}
          style={{
            width: '100%',
            padding: '0.625rem 0.875rem',
            border: '1px solid #D1D5DB',
            borderRadius: '6px',
            fontSize: '0.875rem',
            fontFamily: 'inherit',
            resize: 'vertical'
          }}
        />
      </div>
    </Drawer>
  );
};

export default ApprovalRequestModal;

